import type { RootState } from "~/store";

import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ReactElement } from "react";

import { ApplicationEnum } from "~/@types/enums";
import Config from "~/config";
import { readDisplayModePreference, resolveIsWideScreen } from "~/helpers/displayModePreference";

export const AI_PANEL_WIDTH = 420;
export const PROFILE_PANEL_WIDTH = 440;

export interface BreadCrumb {
	label: string;
	link: string;
}

export interface FerniePrompt {
	opensOnboarding: boolean;
	text: string;
}

export interface LayoutState {
	actionLinks: PageButton[];
	activePanelKey: string;
	aiPanelOpen: boolean;
	aiPanelPoppedOut: boolean;
	application: ApplicationEnum;
	ferniePrompt: FerniePrompt | null;
	gridSearchText: string;
	internetStatus: number;
	isWideScreen: boolean;
	pageBreadcrumb?: BreadCrumb[];
	pageButtons: PageButton[];
	pageDescription?: string;
	pageIcon?: string;
	pageIdentifier?: string;
	pageLabel?: string;
	pageTitle: string | ReactElement;
	panelOpenByKey: Record<string, boolean>;
	preferencesModalOpen: boolean;
	preferencesModalTab: string;
	profilePanelOpen: boolean;
	showTopBar: boolean;
	updateAvailable: boolean;
}

const initialState: LayoutState = {
	actionLinks: [],
	activePanelKey: "",
	aiPanelOpen: false,
	aiPanelPoppedOut: false,
	application: ApplicationEnum.WORKSUITE,
	ferniePrompt: null,
	gridSearchText: "",
	internetStatus: 0,
	isWideScreen: resolveIsWideScreen(window.innerWidth, readDisplayModePreference(), Config.navDrawerCoverWidth),
	pageButtons: [],
	pageTitle: "Worksuite",
	panelOpenByKey: {},
	preferencesModalOpen: false,
	preferencesModalTab: "preferences",
	profilePanelOpen: false,
	showTopBar: true,
	updateAvailable: false
};

export const layoutSlice = createSlice({
	initialState,
	name: "layout",
	reducers: {
		appUpdateAvailable: (state) => {
			state.updateAvailable = true;
		},
		appUpdateNotAvailable: (state) => {
			state.updateAvailable = false;
		},
		clearFerniePrompt: (state) => {
			state.ferniePrompt = null;
		},
		closeAiPanel: (state) => {
			state.aiPanelOpen = false;
			state.aiPanelPoppedOut = false;
		},
		closeAiPanelPopout: (state) => {
			state.aiPanelPoppedOut = false;
		},
		cleanupPanelKey: (state, action: PayloadAction<string>) => {
			const key = action.payload;
			const subTabPrefix = `${key}:`;
			for (const existingKey of Object.keys(state.panelOpenByKey)) {
				if (existingKey === key || existingKey.startsWith(subTabPrefix)) {
					delete state.panelOpenByKey[existingKey];
				}
			}
		},
		closePanel: (state) => {
			state.panelOpenByKey[state.activePanelKey] = false;
		},
		closePreferencesModal: (state) => {
			state.preferencesModalOpen = false;
		},
		closeProfilePanel: (state) => {
			state.profilePanelOpen = false;
		},
		hideTopBar: (state) => {
			state.showTopBar = false;
		},
		openPanel: (state) => {
			state.panelOpenByKey[state.activePanelKey] = true;
		},
		openPreferencesModal: (state, action: PayloadAction<string | undefined>) => {
			state.preferencesModalOpen = true;
			state.preferencesModalTab = action.payload ?? "preferences";
		},
		openAiPanelPopout: (state) => {
			state.aiPanelOpen = true;
			state.aiPanelPoppedOut = true;
		},
		openAiPanelWithPrompt: (state, action: PayloadAction<FerniePrompt>) => {
			state.aiPanelOpen = true;
			state.ferniePrompt = action.payload;
		},
		setActivePanelKey: (state, action: PayloadAction<string>) => {
			state.activePanelKey = action.payload;
		},
		setPageBreadcrumb: (state, action: PayloadAction<BreadCrumb[] | null>) => {
			state.pageBreadcrumb = action.payload ?? undefined;
		},
		setPageDescription: (state, action: PayloadAction<string | null>) => {
			state.pageDescription = action.payload ?? undefined;
		},
		setPageIcon: (state, action: PayloadAction<string | null>) => {
			state.pageIcon = action.payload ?? undefined;
		},
		setPageIdentifier: (state, action: PayloadAction<string | null>) => {
			state.pageIdentifier = action.payload ?? undefined;
		},
		setPageLabel: (state, action: PayloadAction<string | null>) => {
			state.pageLabel = action.payload ?? undefined;
		},
		showTopBar: (state) => {
			state.showTopBar = true;
		},
		toggleAiPanel: (state) => {
			state.aiPanelOpen = !state.aiPanelOpen;
			if (!state.aiPanelOpen) state.aiPanelPoppedOut = false;
		},
		togglePanel: (state) => {
			state.panelOpenByKey[state.activePanelKey] = !state.panelOpenByKey[state.activePanelKey];
		},
		toggleProfilePanel: (state) => {
			state.profilePanelOpen = !state.profilePanelOpen;
		},
		updateActionLinks: (state, action: PayloadAction<PageButton[]>) => {
			state.actionLinks = action.payload;
		},
		updateApplication: (state, action: PayloadAction<ApplicationEnum>) => {
			state.application = action.payload;
		},
		updateGridSearchText: (state, action: PayloadAction<string>) => {
			state.gridSearchText = action.payload;
		},
		updateInternetStatus: (state, action: PayloadAction<number>) => {
			state.internetStatus = action.payload;
		},
		updateIsWideScreen: (state, action: PayloadAction<boolean>) => {
			state.isWideScreen = action.payload;
		},
		updatePageButtons: (state, action: PayloadAction<PageButton[]>) => {
			state.pageButtons = action.payload;
		},
		updatePageTitle: (state, action: PayloadAction<string | ReactElement>) => {
			state.pageTitle = action.payload;
		}
	}
});

export const selectPanelOpen = (state: RootState): boolean =>
	state.layout.panelOpenByKey[state.layout.activePanelKey] ?? false;

export const {
	appUpdateAvailable,
	appUpdateNotAvailable,
	cleanupPanelKey,
	clearFerniePrompt,
	closeAiPanel,
	closeAiPanelPopout,
	closePanel,
	closePreferencesModal,
	closeProfilePanel,
	hideTopBar,
	openAiPanelPopout,
	openAiPanelWithPrompt,
	openPanel,
	openPreferencesModal,
	setActivePanelKey,
	setPageBreadcrumb,
	setPageDescription,
	setPageIcon,
	setPageIdentifier,
	setPageLabel,
	showTopBar,
	toggleAiPanel,
	togglePanel,
	toggleProfilePanel,
	updateActionLinks,
	updateApplication,
	updateGridSearchText,
	updateInternetStatus,
	updateIsWideScreen,
	updatePageButtons,
	updatePageTitle
} = layoutSlice.actions;

export default layoutSlice.reducer;
